import { Fragment, type ReactNode } from 'react';
import { motion, type Variants } from 'motion/react';
import { ArrowUpRight, BookOpen } from 'lucide-react';
import { LEGAL_LINKS } from '@reflex/shared/external-links';
import { ReflexMark } from '@reflex/ui/components/brand/ReflexMark';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { MaturityBadge } from '@reflex/ui/components/shared/MaturityBadge';
import { AuthShaderBackground } from './AuthShaderBackground';
import { PRODUCT_MATURITY_STAGE } from '@/lib/product-stage';

// Soft, staggered reveal for the brand crown — the mark rises and sharpens
// first, then the wordmark + stage chip, so the lockup assembles with intent
// rather than snapping in. One-shot on mount; deliberately understated.
const crownVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const crownItemVariants: Variants = {
  hidden: { opacity: 0, y: 8, filter: 'blur(5px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  },
};

interface AuthLayoutProps {
  children: ReactNode;
  /**
   * Optional short line under the wordmark. Omitted by default — the card
   * itself carries the contextual title/subtitle, so a marketing tagline
   * here just competes with it and makes the header feel disjointed.
   */
  tagline?: string;
}

/**
 * Shared surface treatment for the auth card. Lifts the card off the
 * animated backdrop with a frosted, softly-shadowed panel so the content
 * stays the focal point. The fill is intentionally semi-transparent (and
 * relies on `backdrop-blur-xl` to stay legible) so the aurora + data-ocean
 * particles read faintly through it, with a light `border-white/15` edge.
 */
export const authCardClassName =
  'w-full max-w-sm rounded-2xl border border-white/15 bg-card/45 shadow-[0_30px_80px_-28px_rgba(0,0,0,0.55)] backdrop-blur-xl';

/**
 * Branded frame shared by every auth surface (`/login`, `/signup`, and the
 * "already signed in" card). Wraps the auth card in the Reflex brand lockup
 * plus a subtle backdrop — a brand-green glow over a faint grid — so the
 * sign-in flow feels like part of the product rather than a bare form.
 *
 * The framing leans on existing Reflex primitives ({@link ReflexMark},
 * {@link PanelTitle}, design tokens) and uses the baked-in brand green only
 * as a low-opacity accent, keeping it on-pattern in both themes.
 */
export function AuthLayout({ children, tagline }: AuthLayoutProps) {
  const year = new Date().getFullYear();
  return (
    // `dark` is pinned here so the entire auth surface (backdrop, card, text)
    // renders in dark mode in both themes, matching the always-dark aurora.
    <div className="dark relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-background p-4">
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* Animated WebGL aurora (falls back to a transparent canvas, so the
            static glow + grid below remain the backdrop if WebGL is absent). */}
        <AuthShaderBackground />
        {/* Static brand-green glow — also the WebGL fallback floor. Centered on
            the viewport so the brightest pool sits behind the sign-in card (the
            focal point) and falls off evenly toward the top and bottom, rather
            than pooling at the top of tall (mobile) screens. */}
        <div
          className="absolute left-1/2 top-1/2 h-[560px] w-[820px] max-w-[150vw] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
          style={{
            background:
              'radial-gradient(circle at 50% 50%, rgba(104, 236, 196, 0.12), transparent 70%)',
          }}
        />
        {/* Faint grid, masked to fade toward the edges (centered on the card). */}
        <div
          className="absolute inset-0 [mask-image:radial-gradient(ellipse_62%_52%_at_50%_46%,#000_25%,transparent_78%)]"
          style={{
            backgroundImage:
              'linear-gradient(to right, rgba(127, 127, 140, 0.10) 1px, transparent 1px), linear-gradient(to bottom, rgba(127, 127, 140, 0.10) 1px, transparent 1px)',
            backgroundSize: '38px 38px',
          }}
        />
      </div>

      <div className="relative flex w-full max-w-sm flex-col items-center">
        {/* Brand lockup — the app tile is already a self-framed mark, so we
            don't wrap it in a second box (a box-in-a-box reads as clunky and
            leaves dead space). Instead a soft brand-green halo sits behind the
            lockup as a backdrop, grounding it on the animated aurora while
            keeping it visually separate from the card below. */}
        {/* Brand crown: a centered vertical lockup (mark → wordmark → stage
            chip) that sits above the card on the aurora. Stacking vertically
            puts the wordmark on the page's center axis (so it lines up with the
            card title below) and lets the mark + wordmark scale up to clearly
            out-weigh the card's "Welcome back" — the page identity should read
            as the top of the hierarchy, not an afterthought. Emphasis comes
            from a brand-green glow + a hairline gradient frame, not extra size. */}
        <motion.div
          variants={crownVariants}
          initial="hidden"
          animate="show"
          className="relative mb-8 flex flex-col items-center gap-3"
        >
          <div
            aria-hidden
            className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-44 w-64 max-w-[80vw] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(104, 236, 196, 0.2), transparent 70%)',
            }}
          />
          {/* Logo mark wrapped in a hairline top-lit gradient frame and lifted
              on a brand-green-tinted shadow so it reads as a crafted, elevated
              brand object rather than a flat icon. */}
          <motion.div
            variants={crownItemVariants}
            className="rounded-[19px] bg-gradient-to-b from-white/30 via-white/[0.06] to-transparent p-px shadow-[0_16px_48px_-12px_rgba(104,236,196,0.45)]"
          >
            <ReflexMark
              variant="tile"
              className="size-14 rounded-[18px] shadow-lg shadow-black/50"
            />
          </motion.div>
          <motion.div variants={crownItemVariants} className="flex flex-col items-center gap-2">
            {/* Wordmark: a top-lit white gradient with a soft brand-green glow
                beneath the glyphs, tying the name to the aurora and giving it
                presence without a heavier weight. */}
            <PanelTitle
              as="span"
              className="bg-gradient-to-b from-white via-white to-white/55 bg-clip-text text-2xl font-semibold leading-none tracking-tight text-transparent [filter:drop-shadow(0_1px_10px_rgba(104,236,196,0.28))]"
            >
              Reflex
            </PanelTitle>
            {/* Stage chip centered under the wordmark — a frosted, hue-tinted
                pill with a fine inset top highlight so it catches light like
                the card edges. */}
            <MaturityBadge stage={PRODUCT_MATURITY_STAGE} size="pill" />
          </motion.div>
        </motion.div>
        {tagline && (
          <p className="mb-6 -mt-3 max-w-[18rem] text-center text-sm leading-snug text-muted-foreground">
            {tagline}
          </p>
        )}

        {children}

        {/* "Learn more" affordance — a low-key path to the docs for visitors
            who want context before signing in. Framed below the card and
            styled like the frosted panel so it reads as part of the surface,
            not a stray link. Opens in a new tab so the auth flow is preserved
            (the standalone `/docs` shell renders regardless of auth state). */}
        <a
          href="/docs"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="auth-docs-link"
          className="group mt-6 inline-flex items-center gap-2 rounded-full border border-border/40 bg-card/35 py-1.5 pl-3 pr-3.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur-md transition-colors hover:border-border hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <BookOpen className="icon-sm text-primary/80 transition-colors group-hover:text-primary" />
          Want to learn more? Read the docs
          <ArrowUpRight className="icon-xs text-muted-foreground/70 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
        </a>
      </div>

      <div className="absolute inset-x-0 bottom-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-2xs text-muted-foreground/70">
        <span>© {year} Runloop AI, Inc</span>
        {LEGAL_LINKS.map((link) => (
          <Fragment key={link.key}>
            <span aria-hidden className="hidden sm:inline">
              ·
            </span>
            <a
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`auth-legal-${link.key}-link`}
              className="underline decoration-muted-foreground/30 underline-offset-4 transition-colors hover:text-foreground hover:decoration-foreground/60"
            >
              {link.label}
            </a>
          </Fragment>
        ))}
      </div>
    </div>
  );
}
